import React from 'react';
import { Empty, InputNumber, Table, Tag, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { EditableFee, EditableFeeField } from '../../types';
import { isReverseMargin } from '../../utils/feeChanges';
import { FeeTableStyle } from './styles';

type FeeTableProps = {
  fees: EditableFee[];
  canUpdate: boolean;
  isLoading: boolean;
  onChange: (id: number, field: EditableFeeField, value: number | null) => void;
};

/** 요율은 소수 둘째 자리까지, 금액은 정수 원 단위로 받는다. */
const rateProps = { min: 0, max: 100, step: 0.01, precision: 2, addonAfter: '%' };
const wonProps = { min: 0, step: 100, precision: 0, addonAfter: '원' };

/**
 * 같은 PG 사 행을 세로로 병합한다.
 * 19행이 평평하게 늘어서면 어느 PG 를 보는 중인지 놓친다.
 */
const getPgRowSpan = (fees: EditableFee[], index: number): number => {
  const { pgProviderId } = fees[index];
  if (index > 0 && fees[index - 1].pgProviderId === pgProviderId) return 0;
  return fees.filter((fee) => fee.pgProviderId === pgProviderId).length;
};

export const FeeTable = (props: FeeTableProps): React.JSX.Element => {
  const { fees, canUpdate, isLoading, onChange } = props;

  const numberCell = (
    record: EditableFee,
    field: EditableFeeField,
    label: string,
    inputProps: Record<string, unknown>,
  ): React.JSX.Element => (
    <InputNumber
      className="fee-input"
      // 🔴 조절기(스테퍼)를 끈다. 레거시 antd v3 전역 스타일(custom_antd.scss)이 핸들러를
      // 불투명 배경으로 입력칸 위에 겹쳐 그려서 hover 시 값이 가려진다. 요율·금액은 직접
      // 입력하는 값이라 0.01 씩 올리는 버튼의 쓸모도 없다.
      controls={false}
      aria-label={`${record.pgProviderKorName} ${record.payMethodName} ${label}`}
      // 미사용 PG 는 값을 바꿀 이유가 없다. 과거 정산이 참조해 지울 수도 없으니 읽기 전용으로 둔다.
      disabled={!canUpdate || record.isDormant}
      value={record[field]}
      onChange={(value) => onChange(record.id, field, value === undefined ? null : value)}
      {...inputProps}
    />
  );

  const settlementColumn = (
    title: string,
    field: EditableFeeField,
    inputProps: Record<string, unknown>,
    position: 'start' | 'middle' | 'end',
  ) => ({
    title,
    dataIndex: field,
    width: 170,
    className: `fee-col-settlement${position === 'start' ? ' fee-col-group-start' : ''}${
      position === 'end' ? ' fee-col-group-end' : ''
    }`,
    render: (_: unknown, record: EditableFee) => numberCell(record, field, title, inputProps),
  });

  const columns: ColumnsType<EditableFee> = [
    {
      title: 'PG 사',
      dataIndex: 'pgProviderKorName',
      width: 140,
      fixed: 'left',
      className: 'fee-col-pg',
      onCell: (_, index) => ({ rowSpan: getPgRowSpan(fees, index ?? 0) }),
      render: (name: string, record) => (
        <span className="fee-pg-name">
          {name}
          {record.isDormant && (
            <Tooltip
              title={
                record.lastPaymentDate
                  ? `마지막 결제 ${dayjs(record.lastPaymentDate).format('YYYY.MM.DD')} · 수정할 수 없습니다`
                  : '결제 이력이 없습니다 · 수정할 수 없습니다'
              }
            >
              <Tag bordered={false}>미사용</Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: '결제수단',
      dataIndex: 'payMethodName',
      width: 190,
      fixed: 'left',
      render: (name: string) => <span className="fee-method-name">{name}</span>,
    },
    {
      title: <span className="fee-group-title">정산용 — 브랜드 정산금</span>,
      className: 'fee-col-settlement fee-col-group-start fee-col-group-end',
      children: [
        {
          title: '명목 요율',
          dataIndex: 'feeRate',
          width: 170,
          className: 'fee-col-settlement fee-col-group-start',
          render: (_: unknown, record: EditableFee) => (
            <>
              {numberCell(record, 'feeRate', '명목 요율', rateProps)}
              {isReverseMargin(record) && (
                <Tooltip title="명목 요율이 실제 요율보다 낮습니다. 우리가 손해를 보는 구조입니다.">
                  <div className="fee-reverse-margin">
                    <WarningOutlined /> 역마진
                  </div>
                </Tooltip>
              )}
            </>
          ),
        },
        settlementColumn('최소 수수료', 'minimumFeeAmount', wonProps, 'middle'),
        settlementColumn('최소 요율 금액', 'minimumFeeRateAmount', wonProps, 'end'),
      ],
    },
    {
      title: <span className="fee-group-title">손익용 — 손익계산서</span>,
      className: 'fee-col-finance fee-col-group-start fee-col-group-end',
      children: [
        {
          title: '실제 요율',
          dataIndex: 'feeRateActual',
          width: 170,
          className: 'fee-col-finance fee-col-group-start',
          render: (_: unknown, record: EditableFee) =>
            numberCell(record, 'feeRateActual', '실제 요율', rateProps),
        },
        {
          title: '실제 최소 수수료',
          dataIndex: 'minimumFeeAmountActual',
          width: 170,
          className: 'fee-col-finance fee-col-group-end',
          render: (_: unknown, record: EditableFee) =>
            numberCell(record, 'minimumFeeAmountActual', '실제 최소 수수료', wonProps),
        },
      ],
    },
  ];

  return (
    <FeeTableStyle>
      <div className="fee-legend">
        <span className="fee-legend-item">
          <span className="fee-legend-swatch is-settlement" aria-hidden />
          <span>
            <b>정산용</b> 브랜드에 지급할 정산금 계산에 쓰입니다. 바꾸면 <b>다음 정산 생성분부터</b> 반영되고, 이미
            만들어진 정산은 바뀌지 않습니다.
          </span>
        </span>
        <span className="fee-legend-item">
          <span className="fee-legend-swatch is-finance" aria-hidden />
          <span>
            <b>손익용</b> 월별 손익의 PG 수수료에 쓰입니다. 정산금에는 영향이 없습니다.
          </span>
        </span>
      </div>
      <Table
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={fees}
        loading={isLoading}
        pagination={false}
        bordered={false}
        scroll={{ x: 1180 }}
        rowClassName={(record) => (record.isDormant ? 'fee-row-dormant' : '')}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="표시할 수수료가 없습니다." /> }}
      />
    </FeeTableStyle>
  );
};
